import { Close, FileDownloadOutlined } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { lazy, Suspense, useState } from 'react';
import { truncateFilename } from '../../../helpers/knowledgeHelper';
import { useKnowledgeFilePreviewUrl } from '../../../hooks/queries/useKnowledgeFilePreviewUrl';
import { useTranslation } from '../../../hooks/useTranslation';
import { KnowledgeFileSearchResult } from '../../../interfaces/Knowledge';
import ActionButton from '../../shared/Button/Button';
import Text from '../../shared/Text/Text';
import HighlightedSnippet from '../KnowledgeFileSearchBar/HighlightedSnippet';
import actionIconButtonStyle from '../KnowledgeFileSearchBar/actionIconButtonStyle';
import type { FindRequest } from './PdfDocumentViewer';
import { snippetFindPhrases } from './snippetFindPhrases';

// pdf.js is large, so it loads with the first preview, not with the page
const PdfDocumentViewer = lazy(() => import('./PdfDocumentViewer'));

const TITLE_ID = 'knowledge-file-preview-title';

// Render with key={file name}: the selected match and errors belong to one file
interface KnowledgeFilePreviewModalProps {
  knowledgeId: string;
  // The search result to preview; null keeps the modal closed
  result: KnowledgeFileSearchResult | null;
  query: string;
  onClose: () => void;
  onDownload: (fileName: string) => void;
}

const KnowledgeFilePreviewModal = ({
  knowledgeId,
  result,
  query,
  onClose,
  onDownload,
}: KnowledgeFilePreviewModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const fileName = result?.file_name;
  const {
    data: previewUrl,
    isLoading,
    isError,
  } = useKnowledgeFilePreviewUrl(knowledgeId, fileName);
  // Opening highlights every occurrence of the query; a snippet narrows it down
  const [findRequest, setFindRequest] = useState<FindRequest>({
    phrases: [query],
    id: 0,
  });
  const [selectedSnippet, setSelectedSnippet] = useState<number | null>(null);
  const [isViewerError, setIsViewerError] = useState(false);

  if (!result) return null;

  const download = () => onDownload(result.file_name);
  const showSnippet = (snippet: string, index: number) => {
    setSelectedSnippet(index);
    setFindRequest(previous => ({
      phrases: snippetFindPhrases(snippet, query),
      id: previous.id + 1,
    }));
  };

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isSmallScreen}
      aria-labelledby={TITLE_ID}
    >
      {/* Own id: by default DialogTitle reuses aria-labelledby, which would
          duplicate the id and name the dialog after the whole header row */}
      <DialogTitle
        id="knowledge-file-preview-header"
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
      >
        {/* Box holds the ref Tooltip needs; Text cannot */}
        <Tooltip title={result.file_name} arrow placement="bottom-start">
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
            <Text
              type="subtitle1"
              id={TITLE_ID}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {truncateFilename(result.file_name)}
            </Text>
          </Box>
        </Tooltip>
        <Tooltip title={t('Download file')} arrow>
          <span>
            <ActionButton
              size="small"
              variant="outlined"
              color="inherit"
              padding="0.5rem"
              style={{ ...actionIconButtonStyle, flexShrink: 0 }}
              aria-label={t('Download')}
              onClick={download}
            >
              <FileDownloadOutlined />
            </ActionButton>
          </span>
        </Tooltip>
        <IconButton aria-label={t('Close preview')} onClick={onClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          height: { md: '75vh' },
        }}
      >
        {/* Styled like WorkflowSidebar, the agent form step list */}
        <Box
          component="section"
          aria-label={t('Matches')}
          sx={{
            width: { md: 280 },
            flexShrink: 0,
            overflowY: 'auto',
            p: 2.5,
            borderRadius: 3,
            bgcolor: '#F1F4FF',
            border: '1px solid rgba(24, 65, 255, 0.08)',
          }}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2.5, fontWeight: 700, wordBreak: 'break-word' }}
          >
            {t('Matches for "{{query}}"', { query })}
          </Typography>
          <Stack component="ul" gap={2} sx={{ listStyle: 'none', p: 0, m: 0 }}>
            {result.snippets.map((snippet, index) => {
              const isSelected = selectedSnippet === index;
              return (
                <li key={index}>
                  <ButtonBase
                    aria-pressed={isSelected}
                    onClick={() => showSnippet(snippet, index)}
                    focusRipple
                    sx={{
                      display: 'flex',
                      gap: 1.25,
                      alignItems: 'flex-start',
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 24,
                        height: 24,
                        borderRadius: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        color: isSelected ? '#FFFFFF' : '#6B7280',
                        bgcolor: isSelected ? '#1841FF' : '#FFFFFF',
                        border: isSelected ? 'none' : '1px solid #D7DCE8',
                        boxShadow: isSelected
                          ? '0 6px 16px rgba(24, 65, 255, 0.28)'
                          : 'none',
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Box sx={{ minWidth: 0, pt: 0.25 }}>
                      <HighlightedSnippet snippet={snippet} query={query} />
                    </Box>
                  </ButtonBase>
                </li>
              );
            })}
          </Stack>
          {result.match_count > result.snippets.length && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 2.5, color: '#6B7280' }}
            >
              {t('Showing {{shown}} of {{count}} matches.', {
                shown: result.snippets.length,
                count: result.match_count,
              })}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 1, color: '#6B7280' }}
          >
            {t('Select a match to jump to it in the file.')}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: '1 1 auto',
            position: 'relative',
            height: { xs: '60vh', md: 'auto' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid rgba(15, 23, 42, 0.08)',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {isLoading && <CircularProgress aria-label={t('Loading preview')} />}
          {(isError || isViewerError) && (
            <Alert
              severity="error"
              action={
                <Button size="small" color="inherit" onClick={download}>
                  {t('Download')}
                </Button>
              }
            >
              {t('The preview could not be loaded.')}
            </Alert>
          )}
          {previewUrl && !isViewerError && (
            <Box
              role="document"
              aria-label={t('Preview of {{file}}', { file: result.file_name })}
              sx={{ position: 'absolute', inset: 0 }}
            >
              <Suspense
                fallback={
                  <CircularProgress
                    aria-label={t('Loading preview')}
                    sx={{ position: 'absolute', top: '50%', left: '50%' }}
                  />
                }
              >
                <PdfDocumentViewer
                  url={previewUrl}
                  findRequest={findRequest}
                  onError={() => setIsViewerError(true)}
                />
              </Suspense>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeFilePreviewModal;
